import React, { useState, useEffect } from 'react';
import realTimeDeviceService from '../../services/realTimeDeviceService';
import useDeviceActiveSessions from '../../hooks/useDeviceActiveSessions';
import ActiveDebugSessions from './ActiveDebugSessions';
import '../assets/RdQuickStart.css';
import '../assets/RtQuickStart.css';

/**
 * RT_STRATEGIES — preset configurations for the two RT quick-start cards.
 *
 * actions uses the frontend monitor-action shape that realTimeDeviceService.startSession()
 * expects: { type, metric, interval, maxIterations }.
 * buildActionPayload() inside the service converts these to the backend shape.
 *
 * liveMetrics lists the actionKey strings to watch in the telemetry/latest response.
 */
export const RT_STRATEGIES = [
  {
    id: 'rsrp-rsrq-sinr',
    icon: '📡',
    label: 'Start RSRP / RSRQ / SINR Monitoring',
    desc: 'Continuous signal quality monitoring — RSRP, RSRQ and SINR, sampled every 10 s.',
    durationMins: 30,
    actions: [
      { type: 'monitor', metric: 'rsrp', interval: '10s', maxIterations: null },
      { type: 'monitor', metric: 'rsrq', interval: '10s', maxIterations: null },
      { type: 'monitor', metric: 'sinr', interval: '10s', maxIterations: null },
    ],
    liveMetrics: ['signal-strength-rsrp', 'signal-strength-rsrq', 'signal-strength-sinr'],
    liveKind: 'signal',
  },
  {
    id: 'serving-cell',
    icon: '📶',
    label: 'Start Serving Cell Metrics',
    desc: 'Snapshot serving cell context — sampled every 10 s, max 3 samples, 30 min session.',
    durationMins: 30,
    actions: [
      { type: 'monitor', metric: 'serving', interval: '10s', maxIterations: '3' },
    ],
    liveMetrics: ['serving-cell-metrics'],
    liveKind: 'serving',
  },
];

const IDLE = { phase: 'idle', message: '' };

/**
 * RtQuickStart — 2-card RT quick-start component for the New Diagnostics Start RT sub-tab.
 *
 * Props:
 *   systemId          {string}   — device system ID; if empty, cards are disabled
 *   userInfo          {string}   — logged-in user's email (passed to service context)
 *   role              {string}   — user role (used for active-session detection)
 *   variant           {'cards'|'full'}  — caller hint; no behavioural difference
 *   onSessionStarted  {function} — optional callback({ strategy, sessionId }) on success
 *   onViewSessions    {function} — optional callback to navigate to RT View tab
 */
function RtQuickStart({ systemId, userInfo, role, variant = 'cards', onSessionStarted, onViewSessions }) {
  const [confirmingId, setConfirmingId] = useState(null);
  const [statuses, setStatuses] = useState(() =>
    Object.fromEntries(RT_STRATEGIES.map((s) => [s.id, IDLE]))
  );

  const [errorMsg, setErrorMsg] = useState('');

  const { rtSessions, hasActiveRt, refresh } = useDeviceActiveSessions(systemId, userInfo, role);

  // Keep service user context in sync
  useEffect(() => {
    if (userInfo) realTimeDeviceService.setUserContext(userInfo, null);
  }, [userInfo]);

  const setStatus = (id, patch) =>
    setStatuses((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const hasSystemId = Boolean(systemId && systemId.trim());

  const handleCardClick = (id) => {
    if (!hasSystemId || hasActiveRt) return;
    const s = statuses[id];
    if (s.phase === 'loading') return;
    if (s.phase === 'idle') setConfirmingId(id);
  };

  const handleCancel = () => setConfirmingId(null);

  const handleConfirm = async () => {
    const strategy = RT_STRATEGIES.find((s) => s.id === confirmingId);
    setConfirmingId(null);
    if (!strategy) return;
    setStatus(strategy.id, { phase: 'loading', message: '' });
    setErrorMsg('');

    try {
      const created = await realTimeDeviceService.startSession({
        systemId: systemId.trim(),
        durationMins: strategy.durationMins,
        scheduled: false,
        actions: strategy.actions,
      });

      const sessionId = created?.sessionId || '';
      setStatus(strategy.id, IDLE);
      if (onSessionStarted) onSessionStarted({ strategy, sessionId });
      refresh();
    } catch (err) {
      setStatus(strategy.id, IDLE);
      setErrorMsg(err?.message || 'Failed to start session — please try again.');
    }
  };

  const confirmingStrategy = confirmingId ? RT_STRATEGIES.find((s) => s.id === confirmingId) : null;

  return (
    <div className="rtqs-root">
      {/* ── Cards ── */}
      <div className="rtqs-grid">
        {RT_STRATEGIES.map(({ id, icon, label, desc }) => {
          const s = statuses[id];
          const isLoading = s.phase === 'loading';
          const isDisabled = !hasSystemId || isLoading || hasActiveRt;

          return (
            <div
              key={id}
              className={`rdqs-card${isLoading ? ' rdqs-card--loading' : ''}${isDisabled ? ' rdqs-card--disabled' : ''}`}
              onClick={() => !isDisabled && handleCardClick(id)}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              onKeyDown={(e) => e.key === 'Enter' && !isDisabled && handleCardClick(id)}
            >
              <div className="rdqs-icon">{icon}</div>
              <span className="rdqs-kind rtqs-kind--rt">Real-Time</span>
              <div className="rdqs-label">{label}</div>
              <div className="rdqs-desc">{desc}</div>

              {isLoading && (
                <div className="rdqs-status rdqs-status--loading">⏳ Starting…</div>
              )}
            </div>
          );
        })}
      </div>

      {!hasSystemId && (
        <p className="rdqs-no-device-hint">No device selected — enter a System ID above.</p>
      )}

      {/* Blocked notice */}
      {hasSystemId && hasActiveRt && (
        <p className="rdqs-no-device-hint" style={{ color: '#92400e' }}>
          ⚠️ An RT session is already running on this device. Only one RT session can run per device — manage it in Active Debug Sessions below.
        </p>
      )}

      {/* Error notice (outside card) */}
      {errorMsg && (
        <p className="rdqs-no-device-hint" style={{ color: '#dc2626' }}>
          ⚠️ {errorMsg}
        </p>
      )}

      {/* Active sessions list */}
      {hasSystemId && (
        <ActiveDebugSessions
          rdSessions={[]}
          rtSessions={rtSessions}
          onViewRt={onViewSessions}
          refresh={refresh}
        />
      )}

      {/* ── Confirm modal ── */}
      {confirmingStrategy && (
        <div className="rdqs-modal-backdrop" onClick={handleCancel}>
          <div className="rdqs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rdqs-modal-header">
              <h3 className="rdqs-modal-title">
                {confirmingStrategy.icon} {confirmingStrategy.label}
              </h3>
              <button className="rdqs-modal-close" onClick={handleCancel} aria-label="Close">✕</button>
            </div>
            <div className="rdqs-modal-body">
              <div className="rdqs-modal-field">
                <span className="rdqs-modal-field-label">Device System ID</span>
                <span className="rdqs-modal-sysid-pill">{systemId}</span>
              </div>
              <div className="rdqs-preset-grid">
                <div className="rdqs-preset-row">
                  <span className="rdqs-preset-key">Duration</span>
                  <span className="rdqs-preset-val">{confirmingStrategy.durationMins} minutes</span>
                </div>
                <div className="rdqs-preset-row">
                  <span className="rdqs-preset-key">Mode</span>
                  <span className="rdqs-preset-val">Immediate start</span>
                </div>
                {confirmingStrategy.actions.map((a, i) => (
                  <div key={i} className="rdqs-preset-row">
                    <span className="rdqs-preset-key">
                      {confirmingStrategy.actions.length > 1 ? `Monitor ${i + 1}` : 'Monitor'}
                    </span>
                    <span className="rdqs-preset-val">
                      {a.metric.toUpperCase()} · every {a.interval}
                      {a.maxIterations ? ` · max ${a.maxIterations}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rdqs-modal-footer">
              <button className="rdqs-btn-go" onClick={handleConfirm}>▶ Start Session</button>
              <button className="rdqs-btn-cancel" onClick={handleCancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RtQuickStart;
